import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';

export const requestHeadersInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const correlationId = createCorrelationId();
  const sessionId = window.sessionStorage.getItem('sessionId') || '';
  let headers = request.headers
    .set('x-fnma-channel', 'web')
    .set('x-fnma-correlation-id', correlationId);

  if (sessionId) {
    headers = headers.set('x-fnma-sessionid', sessionId);
  }

  if (isApiRequest(request.url)) {
    const token = authService.getAccessToken();
    if (token) {
      headers = headers
        .set('Authorization', `Bearer ${token}`)
        .set('x-fnma-jws-token', token);
    }

    const userName = authService.getUserName();
    if (userName) {
      headers = headers.set('x-fnma-username', userName);
    }

    const userDisplayName = authService.getUserDisplayName();
    if (userDisplayName) {
      headers = headers.set('x-fnma-user-display-name', userDisplayName);
    }

    const mornetId = authService.getLenderContext();
    if (mornetId) {
      headers = headers.set('x-fnma-mornet-id', mornetId);
    }

    const sellerServicerCoNo = authService.getSellerServicerCoNo();
    if (sellerServicerCoNo) {
      headers = headers.set('x-fnma-selr-srvr-no', sellerServicerCoNo);
    }

    const userRoles = authService.userRoles();
    if (userRoles.length > 0) {
      headers = headers.set('x-fnma-groups', userRoles.join(','));
    }

    // Extract lender ID from lender-scoped API URLs (e.g. /api/lenders/{lenderId}/financial-submissions/...)
    // and send as X-Selected-Lender-Id header for internal users (external users rely on auth context).
    // Backend uses this to enforce lender scope authorization for analysts/admins.
    const selectedLenderId = extractLenderIdFromUrl(request.url);
    if (selectedLenderId && !mornetId) {
      // Only send the header for internal users (no mornetId); external users are scoped by auth context
      headers = headers.set('X-Selected-Lender-Id', selectedLenderId);
    }

    // Local-dev only: carries the actually-selected dev user identity so the backend can
    // stamp audit fields (e.g. Preparer/Analyst name) correctly instead of falling back to
    // the hardcoded "local-dev" sentinel principal. Backend only honors these headers when
    // security is already bypassed/mocked for local dev — never trusted for real auth.
    const userId = authService.getUserId();
    if (userId) {
      headers = headers.set('X-Dev-User-Id', userId);
    }
    const displayName = authService.getUserDisplayName();
    if (displayName) {
      headers = headers.set('X-Dev-User-Name', displayName);
    }
  }

  if (!(request.body instanceof FormData) && !headers.has('Content-Type') && request.body != null) {
    headers = headers.set('Content-Type', 'application/json');
  }

  return next(request.clone({ headers, withCredentials: true }));
};

/**
 * Extract lender ID from lender-scoped URLs like /api/lenders/{lenderId}/financial-submissions/...
 * Returns the lender ID as a string, or null if the URL is not lender-scoped.
 */
function extractLenderIdFromUrl(url: string): string | null {
  // Match pattern: /api/lenders/{lenderId}/...
  const match = url.match(/\/api\/lenders\/(\d+)/);
  return match ? match[1] : null;
}

function isApiRequest(url: string): boolean {
  return url.startsWith('/api') || url.includes('/api/');
}


function createCorrelationId(): string {
  if ('randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  const randomPart = Math.random().toString(36).slice(2);
  return `cid-${Date.now()}-${randomPart}`;
}